import { motion } from "framer-motion";
import { ArrowRight, PencilRuler, Scan } from "lucide-react";
import ShopButton from "@/components/mall/ShopButton";
import { BillboardFrame, Bullets, Lines, MoreDetails, RoomNumber } from "@/components/mall/BillboardKit";
import { ArtWall } from "@/components/mall/rooms/art/ArtWall";

const BLUE = "#60A5FA";

const FILES = [
    "A high-resolution PNG with a transparent background works best.",
    "Vector files work too: AI, EPS or SVG.",
    "We print exactly what you send us, so print-ready files come out best.",
    "If your image is low quality, we recreate it with AI for $24.",
];

const PRICES = [
    ["We check your file", "$5"],
    ["We clean up your file", "$10 to $50"],
    ["We rebuild your file from scratch", "$50 to $200"],
    ["We recreate a bad file with AI", "$24"],
];

/** Room 05 — the artwork department, hung on a wall of art. */
export default function Artwork() {
    return (
        <BillboardFrame testid="frame-artwork" room="artwork" kind="wide" note="every shop with designers">
        <section className="relative overflow-hidden bg-zinc-100 py-8 md:py-10" data-testid="mall-room-artwork">
            <ArtWall />
            <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
                <div className="inline-block rounded bg-white/90 px-3 py-1.5"><RoomNumber n="05" room="Artwork department" accent="#0B0B0B" /></div>

                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    className="mt-5 overflow-hidden rounded-[28px] p-1 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]"
                    style={{ background: "linear-gradient(140deg, rgba(37, 99, 235,0.55), rgba(0,0,0,0.2) 45%, rgba(236,72,153,0.4))" }}>
                    <div className="dark-block overflow-hidden rounded-[24px] bg-brand-ink">
                        <div className="grid items-stretch lg:grid-cols-[1.1fr_0.9fr]">
                            <div className="relative p-6 sm:p-8">
                                <span className="pointer-events-none absolute inset-4 rounded-2xl border border-dashed" style={{ borderColor: "rgba(37, 99, 235,0.25)" }} />
                                <div className="relative">
                                    <p className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.24em]"
                                        style={{ borderColor: "rgba(37, 99, 235,0.45)", background: "rgba(37, 99, 235,0.08)", color: BLUE }}>
                                        <PencilRuler size={12} /> Artwork department
                                    </p>
                                    <h2 className="mt-4 font-display text-3xl font-black leading-[1.02] tracking-tight text-white sm:text-4xl" data-testid="artwork-headline">
                                        SEND US YOUR ARTWORK. <br />WE GET IT READY TO PRINT.
                                    </h2>
                                    <p className="mt-4 max-w-lg text-base text-zinc-300 md:text-lg" data-testid="artwork-body">
                                        A print is only as good as the file behind it. Send us what you have. We set it up for the
                                        exact job you are buying: the right size, the right colors and the background removed. It
                                        prints clean the first time.
                                    </p>

                                    <div className="mt-6 flex flex-wrap gap-3">
                                        <ShopButton room="artwork" tone="green" testid="room-artwork-cta"
                                            title="Which shop should fix your artwork file?" path="/artwork-request">
                                            <PencilRuler size={16} /> Send us your artwork
                                        </ShopButton>
                                        <ShopButton room="artwork" tone="outline" testid="room-artwork-learn"
                                            title="Which shop do you want to send your artwork to?" path="/artwork-request#learn">
                                            <Scan size={15} /> See good and bad files
                                        </ShopButton>
                                    </div>

                                    <p className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: BLUE }}>
                                        A file check is $5. A cleanup is $10 to $50. A rebuild is $50 to $200. <ArrowRight size={12} />
                                    </p>
                                </div>
                            </div>

                            <div className="relative min-h-[220px] overflow-hidden" data-testid="artwork-canvas">
                                <ArtWall inside />
                                <span className="absolute inset-0" style={{ background: "linear-gradient(90deg, #09090b 0%, rgba(9,9,11,0.35) 30%, transparent 60%)" }} />
                                <span className="absolute bottom-5 right-5 rounded-xl border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white backdrop-blur"
                                    style={{ borderColor: "rgba(37, 99, 235,0.5)", background: "rgba(0,0,0,0.55)" }}>
                                    any style prints here
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <MoreDetails testid="artwork-details" className="mt-8 rounded-2xl bg-white/90 px-5 pb-2" label="What we can print from and what artwork costs">
                <div className="grid gap-8 p-1 pb-4 md:grid-cols-2">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">What we can print from</p>
                        <Bullets items={FILES} accent="#0B0B0B" className="mt-4" testid="artwork-files" />
                    </div>
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">What artwork costs</p>
                        <Lines rows={PRICES} accent="#0B0B0B" className="mt-2" testid="artwork-prices" />
                    </div>
                </div>
                </MoreDetails>
            </div>
        </section>
        </BillboardFrame>
    );
}
